import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import BedrifterTab from './BedrifterTab'
import LeadsTab from './LeadsTab'
import KategorierTab from './KategorierTab'
import NaceKoderTab from './NaceKoderTab'
import MineLeadsTab from './MineLeadsTab'
import NyheterTab from './NyheterTab'
import NaceMultiSelect from './NaceMultiSelect'
import CategoryMultiSelect from './CategoryMultiSelect'
import LeadsInfoBox from './LeadsInfoBox'
import { useMineMarkedsomrader } from '../../hooks/useMineMarkedsomrader'
import { Building2, Zap, Star, Settings, Hash, Newspaper, Car, CalendarClock } from 'lucide-react'
import BilerTab from './BilerTab'
import HendelserTab from './HendelserTab'
import { leadsAPI } from './api'
import type { LeadCategory } from './api'

const TABS = ['Bedrifter', 'Biler', 'Hendelser', 'Leads', 'Mine Leads', 'Kategorier', 'NACE-koder', 'Nyheter'] as const
type Tab = typeof TABS[number]

const TAB_ICONS: Record<Tab, React.ReactNode> = {
  'Bedrifter': <Building2 className="w-4 h-4" />,
  'Biler': <Car className="w-4 h-4" />,
  'Hendelser': <CalendarClock className="w-4 h-4" />,
  'Leads': <Zap className="w-4 h-4" />,
  'Mine Leads': <Star className="w-4 h-4" />,
  'Kategorier': <Settings className="w-4 h-4" />,
  'NACE-koder': <Hash className="w-4 h-4" />,
  'Nyheter': <Newspaper className="w-4 h-4" />,
}

// The tab names above are URL values (?tab=Mine Leads); these are their labels.
const TAB_LABEL_KEYS = {
  'Bedrifter': 'page.tabs.bedrifter',
  'Biler': 'page.tabs.biler',
  'Hendelser': 'page.tabs.hendelser',
  'Leads': 'page.tabs.leads',
  'Mine Leads': 'page.tabs.mineLeads',
  'Kategorier': 'page.tabs.kategorier',
  'NACE-koder': 'page.tabs.naceKoder',
  'Nyheter': 'page.tabs.nyheter',
} as const satisfies Record<Tab, string>

interface NaceItem {
  nace_kode: string
  nace_beskrivelse: string | null
}

export default function LeadsPage() {
  const { t } = useTranslation('leads')
  // The view lives in the URL, not in component state. Opening a bedrift or a
  // NACE code unmounts this page, so anything held in useState is gone by the
  // time the user comes back — they landed on page 1 of the Bedrifter tab no
  // matter where they had been. Keeping it in the query string means the back
  // link returns to the exact view, and browser back/forward work as well.
  const [searchParams, setSearchParams] = useSearchParams()

  const tabParam = searchParams.get('tab')
  const activeTab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'Bedrifter'
  const page = Math.max(1, Number(searchParams.get('page')) || 1)

  /** Patch the query string, dropping defaults so the URL stays readable. */
  function updateParams(patch: Record<string, string | number | boolean | undefined>) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === '' || v === false) next.delete(k)
        else next.set(k, String(v))
      }
      return next
    }, { replace: true })
  }

  function setActiveTab(tab: Tab) {
    // Page is per-view; carrying it across tabs would land the user on a page
    // that may not exist in the tab they just opened.
    updateParams({ tab: tab === 'Bedrifter' ? undefined : tab, page: undefined })
  }

  const setPage = (p: number) => updateParams({ page: p === 1 ? undefined : p })

  const { items: markedsomrader, loading: markedsomraderLoading } = useMineMarkedsomrader()
  const [categories, setCategories] = useState<LeadCategory[]>([])

  useEffect(() => {
    leadsAPI.getCategories()
      .then(res => setCategories(res.data))
      .catch(() => {})
  }, [])

  // Applied filter state (what tabs query against), read from the URL so it
  // survives a trip into a detail page.
  const appliedFilters = {
    markedsomrade: searchParams.get('mo') ?? '',
    search: searchParams.get('q') ?? '',
    nace: searchParams.get('nace') ?? '',
    minScore: searchParams.get('score') ? Number(searchParams.get('score')) : ('' as number | ''),
    category: searchParams.get('cat') ?? '',
    mineNace: searchParams.get('mineNace') === '1',
    vehicleClass: searchParams.get('vc') ?? '',
  }

  function applyFilters(f: typeof appliedFilters) {
    // Any filter change invalidates the current page number.
    updateParams({
      mo: f.markedsomrade, q: f.search, nace: f.nace,
      score: f.minScore === '' ? undefined : f.minScore,
      cat: f.category, mineNace: f.mineNace ? '1' : undefined,
      vc: f.vehicleClass || undefined,
      page: undefined,
    })
  }

  // Draft filter state (what the user sees in the inputs). Seeded from the URL
  // so returning from a detail page shows the filters that are actually
  // applied, rather than empty inputs over a filtered grid.
  const [draftMarkedsomrade, setDraftMarkedsomrade] = useState(appliedFilters.markedsomrade)
  const [draftSearch, setDraftSearch] = useState(appliedFilters.search)
  const [draftNaceItems, setDraftNaceItems] = useState<NaceItem[]>(
    () => appliedFilters.nace
      ? appliedFilters.nace.split(',').map(c => ({ nace_kode: c, nace_beskrivelse: null }))
      : [],
  )
  const [draftMinScore, setDraftMinScore] = useState<number | ''>(appliedFilters.minScore)
  const [draftCategories, setDraftCategories] = useState<string[]>(
    () => appliedFilters.category ? appliedFilters.category.split(',') : [],
  )
  const [draftMineNace, setDraftMineNace] = useState(appliedFilters.mineNace)
  const [draftVehicleClass, setDraftVehicleClass] = useState(appliedFilters.vehicleClass)

  // Module-local vehicle filter (#335): decoupled from the app-global
  // VehicleFilterContext — the leads module will branch to more vehicle
  // classes sooner than the rest of the application.
  const vehicleClass = appliedFilters.vehicleClass || undefined

  // Open the module scoped to the user's first markedsomrade rather than to
  // everything. Unscoped, the listings span ~1.09M organizations, which is too
  // many to read and is also the slowest query shape — /scores measured 1.94 s
  // unscoped against 0.64-0.76 s for a real markedsomrade.
  //
  // Applied once, when the areas finish loading. The ref stops a later refresh
  // of the hook from overwriting a selection the user has since made, including
  // a deliberate switch back to "Alle".
  //
  // Skipped when the URL already carries a view, so returning from a detail
  // page cannot overwrite a scope the user chose — including a deliberate
  // "Alle".
  const defaultScopeApplied = useRef(false)
  const [unscopedAllowed, setUnscopedAllowed] = useState(false)
  useEffect(() => {
    if (defaultScopeApplied.current || markedsomrader.length === 0) return
    defaultScopeApplied.current = true
    if (searchParams.toString()) {
      setUnscopedAllowed(true)
      return
    }
    const first = String(markedsomrader[0].id)
    setDraftMarkedsomrade(first)
    applyFilters({ ...appliedFilters, markedsomrade: first })
  }, [markedsomrader])

  // Until the default lands in the URL, the empty URL reads as "Alle", and a
  // list that queried it would ask for every postnummer on each open (#892).
  // Only the URL itself says the default has landed: the router applies it in
  // a transition, a render after any state set alongside it. An empty URL is a
  // real "Alle" when the user has no areas, opened a view that skipped the
  // default, or chose it with Oppdater.
  const scopeReady = !!searchParams.toString() || unscopedAllowed ||
    (!markedsomraderLoading && markedsomrader.length === 0)

  const draftNaceStr = draftNaceItems.map(i => i.nace_kode).join(',')

  const isDirty =
    draftMarkedsomrade !== appliedFilters.markedsomrade ||
    draftSearch !== appliedFilters.search ||
    draftNaceStr !== appliedFilters.nace ||
    draftMinScore !== appliedFilters.minScore ||
    draftCategories.join(',') !== appliedFilters.category ||
    draftMineNace !== appliedFilters.mineNace ||
    draftVehicleClass !== appliedFilters.vehicleClass

  function handleApply() {
    setUnscopedAllowed(true)
    applyFilters({
      markedsomrade: draftMarkedsomrade,
      search: draftSearch,
      nace: draftNaceStr,
      minScore: draftMinScore,
      category: draftCategories.join(','),
      mineNace: draftMineNace,
      vehicleClass: draftVehicleClass,
    })
  }

  function handleReset() {
    // Reset returns to the default scope, not to "Alle" — clearing the
    // markedsomrade would widen the query rather than narrow it.
    const firstArea = markedsomrader.length > 0 ? String(markedsomrader[0].id) : ''
    const empty = {
      markedsomrade: firstArea, search: '', nace: '',
      minScore: '' as const, category: '', mineNace: false,
      vehicleClass: '',
    }
    setDraftMarkedsomrade(firstArea)
    setDraftSearch('')
    setDraftNaceItems([])
    setDraftMinScore('')
    setDraftCategories([])
    setDraftMineNace(false)
    setDraftVehicleClass('')
    applyFilters(empty)
  }

  const showFilters = activeTab === 'Bedrifter' || activeTab === 'Biler' || activeTab === 'Leads' || activeTab === 'NACE-koder' || activeTab === 'Nyheter'
  // Nyheter has no vehicle-class dimension in its queries — hide the control
  // that would silently do nothing there.
  const showVehicleClassFilter = showFilters && activeTab !== 'Nyheter'
  // Biler queries take markedsomrade + kjøretøytype only; Nyheter takes
  // markedsomrade + NACE + mine-NACE only — hide the controls that would
  // silently do nothing on either.
  const showTextFilters = showFilters && activeTab !== 'Biler' && activeTab !== 'Nyheter'
  const showNaceFilter = showFilters && activeTab !== 'Biler'

  const markedsomradeIds = appliedFilters.markedsomrade || undefined
  const scoreValue = typeof appliedFilters.minScore === 'number' ? appliedFilters.minScore : undefined

  // Describe the active scope for the info box. The applied value is a single
  // id today, so a lookup is enough; the join covers a future multi-select.
  const appliedAreas = appliedFilters.markedsomrade
    ? appliedFilters.markedsomrade.split(',')
        .map(id => markedsomrader.find(m => String(m.id) === id))
        .filter((m): m is NonNullable<typeof m> => !!m)
    : []
  const markedsomradeName = appliedAreas.length
    ? appliedAreas.map(m => m.name).join(', ')
    : null
  const postnummerCount = appliedAreas.length
    ? appliedAreas.reduce((n, m) => n + (m.postnumre?.length ?? 0), 0)
    : null
  // The applied filters as lines for the top of a PDF export (#885). Nyheter
  // queries only the markedsområde and NACE, so it gets just those.
  const scopeLine = `${t('page.filters.markedsomrade')}: ${markedsomradeName ?? t('common.all')}`
  const naceLine = appliedFilters.mineNace
    ? `${t('page.filters.naceKoder')}: ${t('page.filters.mine')}`
    : appliedFilters.nace ? `${t('page.filters.naceKoder')}: ${appliedFilters.nace}` : null
  const exportFiltersNyheter = [scopeLine, naceLine].filter((l): l is string => !!l)
  const exportFiltersBedrifter = [
    scopeLine,
    appliedFilters.vehicleClass ? `${t('page.filters.kjoretoytype')}: ${t(appliedFilters.vehicleClass === 'varebil' ? 'common.varebil' : 'common.personbil')}` : null,
    appliedFilters.search ? `${t('common.search')}: ${appliedFilters.search}` : null,
    naceLine,
    appliedFilters.category ? `${t('common.kategori')}: ${appliedFilters.category}` : null,
    scoreValue !== undefined ? `${t('page.filters.minScore')}: ${scoreValue}` : null,
  ].filter((l): l is string => !!l)

  // "Default" means the scope is still the one applied automatically on open.
  const scopeIsDefault =
    markedsomrader.length > 0 &&
    appliedFilters.markedsomrade === String(markedsomrader[0].id)

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('page.title')}</h1>

      {/* Tab bar — underline style */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 gap-1">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <span className="flex items-center gap-2">
              {TAB_ICONS[tab]}
              {t(TAB_LABEL_KEYS[tab])}
            </span>
          </button>
        ))}
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('page.filters.markedsomrade')}</label>
            <select
              value={draftMarkedsomrade}
              onChange={e => setDraftMarkedsomrade(e.target.value)}
              className="input-field text-sm"
            >
              <option value="">{t('common.all')}</option>
              {markedsomrader.map(m => (
                <option key={m.id} value={String(m.id)}>{m.name}</option>
              ))}
            </select>
          </div>
          {showVehicleClassFilter && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('page.filters.kjoretoytype')}</label>
            <select
              value={draftVehicleClass}
              onChange={e => setDraftVehicleClass(e.target.value)}
              className="input-field text-sm"
            >
              <option value="">{t('common.all')}</option>
              <option value="personbil">{t('common.personbil')}</option>
              <option value="varebil">{t('common.varebil')}</option>
            </select>
          </div>
          )}
          {showTextFilters && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('common.search')}</label>
            <input
              type="text"
              value={draftSearch}
              onChange={e => setDraftSearch(e.target.value)}
              placeholder={activeTab === 'Bedrifter' ? t('page.filters.searchCompany') : t('page.filters.searchOrgNr')}
              className="input-field text-sm"
            />
          </div>
          )}
          {showNaceFilter && (
          <div className="min-w-[220px]">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('page.filters.naceKoder')}</label>
            <div className="flex gap-1">
              <div className="flex-1">
                <NaceMultiSelect
                  selected={draftNaceItems}
                  onChange={items => { setDraftNaceItems(items); setDraftMineNace(false) }}
                  disabled={draftMineNace}
                />
              </div>
              <button
                onClick={() => setDraftMineNace(!draftMineNace)}
                className={`px-2 py-1 rounded-sm text-xs whitespace-nowrap self-end ${draftMineNace ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
                title={t('page.filters.mineTitle')}
              >
                {t('page.filters.mine')}
              </button>
            </div>
          </div>
          )}
          {showTextFilters && (
          <div className="w-[180px]">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('common.kategori')}</label>
            <CategoryMultiSelect
              categories={categories}
              selected={draftCategories}
              onChange={setDraftCategories}
            />
          </div>
          )}
          {showTextFilters && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('page.filters.minScore')}</label>
            <input
              type="number"
              value={draftMinScore}
              onChange={e => setDraftMinScore(e.target.value ? Number(e.target.value) : '')}
              placeholder="0"
              min={0}
              className="input-field text-sm w-20"
            />
          </div>
          )}
          <div className="flex items-end gap-2 ml-auto">
            <button
              onClick={handleApply}
              disabled={!isDirty}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isDirty
                  ? 'bg-primary-600 text-white shadow-xs hover:bg-primary-700'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              {t('page.filters.update')}
            </button>
            <button
              onClick={handleReset}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
            >
              {t('common.reset')}
            </button>
          </div>
        </div>
      )}

      <LeadsInfoBox
        tab={activeTab}
        markedsomradeName={markedsomradeName}
        postnummerCount={postnummerCount}
        vehicleFilter={(appliedFilters.vehicleClass || 'alle') as 'personbil' | 'varebil' | 'alle'}
        scopeIsDefault={scopeIsDefault}
        search={appliedFilters.search}
        naceCount={appliedFilters.nace ? appliedFilters.nace.split(',').length : 0}
        categoryCount={appliedFilters.category ? appliedFilters.category.split(',').length : 0}
        minScore={appliedFilters.minScore}
        mineNace={appliedFilters.mineNace}
      />

      {/* Tab content */}
      {activeTab === 'Bedrifter' && (
        <BedrifterTab
          page={page}
          onPageChange={setPage}
          markedsomradeIds={markedsomradeIds}
          search={appliedFilters.search}
          naceFilter={appliedFilters.nace}
          minScore={scoreValue}
          categoryFilter={appliedFilters.category}
          mineNaceKoder={appliedFilters.mineNace}
          vehicleClass={vehicleClass}
          exportFilters={exportFiltersBedrifter}
          scopeReady={scopeReady}
        />
      )}
      {activeTab === 'Leads' && (
        <LeadsTab
          page={page}
          onPageChange={setPage}
          markedsomradeIds={markedsomradeIds}
          search={appliedFilters.search}
          naceFilter={appliedFilters.nace}
          minScore={scoreValue}
          categoryFilter={appliedFilters.category}
          mineNaceKoder={appliedFilters.mineNace}
          vehicleClass={vehicleClass}
        />
      )}
      {activeTab === 'Biler' && (
        <BilerTab
          page={page}
          onPageChange={setPage}
          markedsomradeIds={markedsomradeIds}
          vehicleClass={vehicleClass}
        />
      )}
      {activeTab === 'Hendelser' && (
        <HendelserTab page={page} onPageChange={setPage} />
      )}
      {activeTab === 'Mine Leads' && <MineLeadsTab />}
      {activeTab === 'Kategorier' && <KategorierTab />}
      {activeTab === 'NACE-koder' && <NaceKoderTab page={page} onPageChange={setPage} markedsomradeIds={markedsomradeIds} filterMineNace={appliedFilters.mineNace} />}
      {activeTab === 'Nyheter' && (
        <NyheterTab
          markedsomradeIds={markedsomradeIds}
          naceFilter={appliedFilters.nace}
          mineNaceKoder={appliedFilters.mineNace}
          exportFilters={exportFiltersNyheter}
        />
      )}
    </div>
  )
}
